import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'
import { Bell } from 'lucide-react'
import useNotificationBell from 'hooks/useNotificationBell'
import NotificationsList from 'modules/notifications/NotificationsList'
import colors from 'theme/colors'
import indexPriority from 'theme/z-index'
import { ui } from 'theme/discover'

const PANEL_WIDTH = 380
const PANEL_MAX_HEIGHT = 560
const PANEL_MIN_HEIGHT = 220
const SCREEN_MARGIN = 12
const GAP = 10

/**
 * Onde o painel fica: embaixo do sino, com a borda direita alinhada à dele. Em
 * tela estreita o sino está no meio do header e o painel passaria da borda, então
 * ele encolhe e é empurrado para dentro da tela. Medido pelo próprio botão, vale
 * para qualquer header e largura, sem depender da altura de cada um.
 */
function placePanel(button) {
    const rect = button.getBoundingClientRect()
    const width = Math.min(PANEL_WIDTH, window.innerWidth - SCREEN_MARGIN * 2)
    const left = Math.min(
        Math.max(rect.right - width, SCREEN_MARGIN),
        window.innerWidth - width - SCREEN_MARGIN
    )
    const top = rect.bottom + GAP

    return {
        top,
        left,
        width,
        maxHeight: Math.max(PANEL_MIN_HEIGHT, Math.min(PANEL_MAX_HEIGHT, window.innerHeight - top - SCREEN_MARGIN)),
    }
}

/**
 * O sino dos headers, com o painel de notificações. Sem novidade é o botão
 * calmo de sempre; com novidade ele acende (sino cheio, contador em cima) e
 * balança uma vez.
 *
 * Cada conta tem um feed, escolhido pelo papel (comprador ou produtor, ver
 * `notificationAudience`), com a sua lista e o seu contador.
 *
 * O balanço é de uma vez só, quando a novidade aparece ou o número sobe: em
 * laço viraria ruído, e a cor e o contador seguem avisando depois que o
 * movimento acaba. Com "reduzir movimento" ligado, não balança.
 *
 * O painel só existe aberto: cada abertura busca a lista de novo, e é ela que
 * marca tudo como lido (e zera o contador). Fecha com clique fora, Esc, ao
 * tocar numa notificação ou ao trocar de página.
 *
 * `size` e `spacing` deixam o header que abriga o sino acertar a altura com os
 * vizinhos (o do painel usa a mesma dos outros botões, com o espaço no gap dele).
 *
 * `variant='soft'` é o sino do app (WUIBellButton): disco cinza-claro sem
 * borda, usado no header da home. O padrão continua o contornado do painel.
 *
 * Quem está deslogado ou não tem feed não vê o sino.
 */
export default function NotificationBell({ size = 38, spacing = 10, variant = 'outline' }) {
    const location = useLocation()
    const { enabled, audience, unreadCount } = useNotificationBell()

    const [open, setOpen] = useState(false)
    const [placement, setPlacement] = useState(null)

    const rootRef = useRef(null)
    const buttonRef = useRef(null)

    useEffect(() => {
        setOpen(false)
    }, [location.pathname])

    useEffect(() => {
        if (!open) return undefined

        function onPointerDown(event) {
            if (!rootRef.current?.contains(event.target)) setOpen(false)
        }

        function onKeyDown(event) {
            if (event.key !== 'Escape') return

            setOpen(false)
            buttonRef.current?.focus()
        }

        function onResize() {
            if (buttonRef.current) setPlacement(placePanel(buttonRef.current))
        }

        document.addEventListener('pointerdown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        window.addEventListener('resize', onResize)

        return () => {
            document.removeEventListener('pointerdown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('resize', onResize)
        }
    }, [open])

    if (!enabled) return null

    function toggle() {
        if (open) {
            setOpen(false)
            return
        }

        setPlacement(placePanel(buttonRef.current))
        setOpen(true)
    }

    const hasUnread = unreadCount > 0
    const label = hasUnread
        ? `Notificações, ${unreadCount} ${unreadCount === 1 ? 'não lida' : 'não lidas'}`
        : 'Notificações'

    return (
        <Root ref={rootRef} $spacing={spacing}>
            <BellButton
                ref={buttonRef}
                type='button'
                title='Notificações'
                aria-label={label}
                aria-haspopup='dialog'
                aria-expanded={open}
                $size={size}
                $soft={variant === 'soft'}
                $active={hasUnread || open}
                onClick={toggle}
            >
                {/* `key` remonta o ícone quando o número muda, e com ele o balanço. */}
                <BellIcon key={unreadCount} $swing={hasUnread}>
                    <Bell size={19} fill={hasUnread ? 'currentColor' : 'none'} aria-hidden='true' />
                </BellIcon>

                {hasUnread && (
                    <Badge aria-hidden='true'>{unreadCount > 99 ? '99+' : unreadCount}</Badge>
                )}
            </BellButton>

            {open && placement && (
                <Panel role='dialog' aria-label='Notificações' style={placement}>
                    <PanelHeader>Notificações</PanelHeader>
                    <NotificationsList audience={audience} onNavigate={() => setOpen(false)} />
                </Panel>
            )}
        </Root>
    )
}

// O sino pendura pelo topo: girar em torno do centro faria a base "andar" para
// os lados, então o eixo fica na borda de cima. Cada ida e volta é menor que a
// anterior, como um sino que para.
const swing = keyframes`
    0%   { transform: rotate(0deg); }
    18%  { transform: rotate(16deg); }
    40%  { transform: rotate(-13deg); }
    62%  { transform: rotate(9deg); }
    82%  { transform: rotate(-5deg); }
    100% { transform: rotate(0deg); }
`

const panelIn = keyframes`
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
`

const Root = styled.div`
    display: flex;
    flex-shrink: 0;
    margin-right: ${({ $spacing }) => $spacing}px;
`

const BellIcon = styled.span`
    display: flex;
    transform-origin: 50% 0;

    ${({ $swing }) => $swing && css`
        animation: ${swing} 0.9s ease-in-out 0.4s 1 both;
    `}

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`

const BellButton = styled.button`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${({ $size }) => $size}px;
    height: ${({ $size }) => $size}px;
    padding: 0;
    border-radius: 50%;
    border: 1.5px solid ${({ $active }) => ($active ? `${colors.occasionalPurple}55` : `${colors.occasionalPurple}33`)};
    background: ${({ $active }) => ($active ? `${colors.occasionalPurple}14` : 'transparent')};
    color: ${colors.occasionalPurple};
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

    &:hover {
        background: ${({ $active }) => ($active ? `${colors.occasionalPurple}22` : `${colors.occasionalPurple}10`)};
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }

    ${({ $soft, $active }) => $soft && css`
        border: none;
        color: ${$active ? ui.link : ui.ink};
        background: ${$active ? ui.tagBg : ui.surfaceMuted};

        &:hover {
            background: ${ui.tagBg};
            color: ${ui.link};
        }
    `}
`

const Badge = styled.span`
    position: absolute;
    top: -5px;
    right: -5px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    box-sizing: border-box;
    border: 2px solid ${colors.neutralBackground};
    background: ${colors.danger};
    color: ${colors.neutralLight};
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
    text-align: center;
    pointer-events: none;
`

const Panel = styled.div`
    position: fixed;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 14px;
    border: 1px solid rgba(112, 112, 112, 0.12);
    background: ${colors.neutralLight};
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.14);
    z-index: ${indexPriority.dropdown};
    animation: ${panelIn} 0.15s ease-out;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`

const PanelHeader = styled.div`
    flex-shrink: 0;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(112, 112, 112, 0.12);
    font-size: 15px;
    font-weight: 700;
    color: ${colors.nightDark};
`
